/* ==========================================================================
   Bloomkind theme — product page
   <product-info> owns the selected variant for one product section. Pickers
   (<variant-picker>, <offer-selector>) report choices to it; it updates the
   form, prices, buttons, stock lines, gallery and URL from pre-rendered data.
   ========================================================================== */
(() => {
  'use strict';
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = (sel) => { const el = this.querySelector(sel); try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; } };
      this.variants = json('[data-variants]') || [];
      this.strings = json('[data-strings]') || {};
      const input = this.querySelector('[data-variant-id]');
      this.current = this.variants.find((v) => input && String(v.id) === input.value) || this.variants[0];
      this.addEventListener('variant:select', (event) => this.select(event.detail.id, event.target));
      this.addEventListener('variant:options', (event) => {
        const match = this.variants.find((v) => v.options.every((o, i) => o === event.detail.options[i]));
        this.select(match ? match.id : null, event.target, event.detail.options);
      });
      this.querySelectorAll('variant-picker').forEach((p) => p.refreshAvailability && p.refreshAvailability(this.variants, this.current));
    }

    select(id, source, options) {
      const variant = this.variants.find((v) => v.id === Number(id)) || null;
      this.current = variant;
      this.querySelectorAll('variant-picker, offer-selector').forEach((el) => {
        if (el !== source && el.sync) el.sync(variant);
        if (el.refreshAvailability) el.refreshAvailability(this.variants, variant, options);
      });
      this.update(variant);
      document.dispatchEvent(new CustomEvent('bk:variant:change', { detail: { sectionId: this.dataset.section, variant } }));
    }

    update(variant) {
      const s = this.strings;
      const sectionId = this.dataset.section;
      const form = document.getElementById(`ProductForm-${sectionId}`);
      const idInput = form ? form.querySelector('[name="id"]') : null;
      if (idInput && variant) { idInput.value = variant.id; idInput.disabled = false; }

      this.querySelectorAll('[data-price-slot]').forEach((slot) => { if (variant) slot.innerHTML = variant.priceHtml; });
      this.querySelectorAll('[data-sticky-price]').forEach((el) => { if (variant) el.textContent = variant.priceText; });

      document.querySelectorAll(`[form="ProductForm-${sectionId}"][data-atc], #ProductForm-${sectionId} [data-atc]`).forEach((btn) => {
        const label = btn.querySelector('[data-atc-label]');
        const available = variant && variant.available;
        btn.setAttribute('aria-disabled', available ? 'false' : 'true');
        if (label) label.textContent = !variant ? s.unavailable : available ? (label.dataset.label || s.addToCart) : s.soldOut;
      });

      this.querySelectorAll('stock-status').forEach((el) => el.render && el.render(variant, s));

      if (variant && this.hasAttribute('data-media-sync') && variant.media) {
        const gallery = this.querySelector('product-gallery');
        if (gallery) gallery.showMedia(variant.media);
      }

      if (variant && this.dataset.url && !(window.Shopify && window.Shopify.designMode)) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }
  define('product-info', ProductInfo);

  /* Buttons or dropdowns, one group per product option. */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => {
        this.querySelectorAll('fieldset').forEach((fs) => {
          const checked = fs.querySelector('input:checked');
          const label = fs.querySelector('[data-selected-value]');
          if (checked && label) label.textContent = checked.value;
        });
        this.dispatchEvent(new CustomEvent('variant:options', { bubbles: true, detail: { options: this.selected() } }));
      });
    }
    selected() {
      const values = [];
      this.querySelectorAll('[data-option-index]').forEach((el) => {
        const i = Number(el.dataset.optionIndex);
        if (el.tagName === 'SELECT') values[i] = el.value;
        else if (el.checked) values[i] = el.value;
      });
      return values;
    }
    sync(variant) {
      if (!variant) return;
      variant.options.forEach((value, i) => {
        this.querySelectorAll(`[data-option-index="${i}"]`).forEach((el) => {
          if (el.tagName === 'SELECT') el.value = value;
          else el.checked = el.value === value;
        });
      });
    }
    /* Mark values that can't form an available variant with the other current choices. */
    refreshAvailability(variants, current, options) {
      const chosen = options || (current ? current.options : this.selected());
      this.querySelectorAll('[data-option-index]').forEach((el) => {
        const i = Number(el.dataset.optionIndex);
        const test = (value) => variants.some((v) => v.available && v.options[i] === value && v.options.every((o, n) => n === i || n > i || o === chosen[n]));
        if (el.tagName === 'SELECT') {
          Array.from(el.options).forEach((opt) => {
            const ok = test(opt.value);
            opt.textContent = opt.value + (ok ? '' : ` (${window.Bloomkind.strings.soldOut})`);
          });
        } else {
          el.closest('label').classList.toggle('is-unavailable', !test(el.value));
        }
      });
    }
  }
  define('variant-picker', VariantPicker);

  /* Offer rows / cards: each radio value is a variant id. */
  class OfferSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('.offer__input');
        if (input) this.dispatchEvent(new CustomEvent('variant:select', { bubbles: true, detail: { id: input.value } }));
      });
    }
    sync(variant) {
      if (!variant) return;
      const input = this.querySelector(`.offer__input[value="${variant.id}"]`);
      if (input) input.checked = true;
    }
  }
  define('offer-selector', OfferSelector);

  /* Gallery: main carousel + thumbnails. */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('bk-carousel');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.carousel && this.carousel.goTo(Number(btn.dataset.thumb))));
      if (this.carousel) {
        this.carousel.addEventListener('carousel:change', (event) => {
          const index = event.detail.index;
          this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
          const active = this.thumbs[index];
          if (active) {
            const list = active.closest('.gallery__thumbs');
            const left = active.offsetLeft - (list.clientWidth - active.clientWidth) / 2;
            list.scrollTo({ left, behavior: 'smooth' });
          }
          this.querySelectorAll('.gallery__slide video').forEach((video, i, all) => {
            const slide = video.closest('.gallery__slide');
            const slides = Array.from(slide.parentElement.children);
            if (slides.indexOf(slide) !== index) video.pause();
          });
        });
      }
    }
    showMedia(mediaId) {
      if (!this.carousel) return;
      const slides = Array.from(this.querySelectorAll('.gallery__slide'));
      const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) this.carousel.goTo(index);
    }
  }
  define('product-gallery', ProductGallery);

  /* Sticky add-to-cart: shown once the main buy button has scrolled above the viewport.
     (A scroll check rather than IntersectionObserver: fast scrolls can jump past the
     button without an intersection change.) */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const main = document.querySelector(`#shopify-section-${this.dataset.section} [data-main-buy]`) || document.querySelector('[data-main-buy]');
      if (!main) return;
      this.hidden = false;
      this.main = main;
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const passed = this.main.getBoundingClientRect().bottom < 0;
          if (passed === this.classList.contains('is-visible')) return;
          this.classList.toggle('is-visible', passed);
          this.setAttribute('aria-hidden', passed ? 'false' : 'true');
          this.querySelectorAll('button').forEach((b) => { b.tabIndex = passed ? 0 : -1; });
          document.documentElement.style.setProperty('--sticky-offset', passed ? `${this.offsetHeight}px` : '0px');
        });
      };
      this.setAttribute('aria-hidden', 'true');
      this.querySelectorAll('button').forEach((b) => { b.tabIndex = -1; });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.documentElement.style.setProperty('--sticky-offset', '0px');
    }
  }
  define('sticky-atc', StickyAtc);

  /* Delivery estimate in working days, calculated in the visitor's browser. */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      const skip = d.skipWeekends === 'true';
      const locale = d.locale || 'en-GB';
      const add = (days) => {
        const date = new Date();
        let left = Number(days) || 0;
        while (left > 0) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (!skip || (day !== 0 && day !== 6)) left -= 1;
        }
        return date;
      };
      let fmt;
      try { fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
      const range = (a, b) => {
        const from = fmt.format(add(a));
        const to = fmt.format(add(Math.max(a, b)));
        return from === to ? from : `${from} – ${to}`;
      };
      const dispatch = this.querySelector('[data-date="dispatch"]');
      const delivery = this.querySelector('[data-date="delivery"]');
      if (dispatch) dispatch.textContent = range(d.dispatchMin, d.dispatchMax);
      if (delivery) delivery.textContent = range(d.deliveryMin, d.deliveryMax);
    }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* Stock line + optional countdown to a real end date. */
  class StockStatus extends HTMLElement {
    connectedCallback() {
      const timer = this.querySelector('[data-countdown]');
      if (!timer) return;
      const end = Number(timer.dataset.countdown) * 1000;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        let s = Math.floor((end - Date.now()) / 1000);
        if (s <= 0) { timer.remove(); return; }
        const days = Math.floor(s / 86400); s -= days * 86400;
        const h = Math.floor(s / 3600); s -= h * 3600;
        const m = Math.floor(s / 60); s -= m * 60;
        timer.textContent = `${timer.dataset.prefix} ${days > 0 ? `${days}d ` : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
        this.timeout = setTimeout(tick, 1000);
      };
      tick();
    }
    disconnectedCallback() { clearTimeout(this.timeout); }
    render(variant, strings) {
      const line = this.querySelector('[data-stock-line]');
      if (!line) return;
      if (!variant || !variant.tracked) { line.hidden = true; return; }
      let html = '';
      if (!variant.available) html = `<span class="stock__dot stock__dot--out"></span>${strings.outOfStock}`;
      else if (variant.lowStock) html = `<span class="stock__dot stock__dot--low"></span>${strings.lowStock.replace('[count]', variant.lowStock)}`;
      else if (this.dataset.showInStock === 'true') html = `<span class="stock__dot"></span>${strings.inStock}`;
      line.innerHTML = html;
      line.hidden = !html;
    }
  }
  define('stock-status', StockStatus);
})();
